import React, {
    Dispatch,
    MutableRefObject,
    ReactElement,
    ReactNode,
    SetStateAction,
    createRef,
    useEffect,
    useRef,
    useState,
} from 'react';
import { FmtpError, GeneralParcelInfo, Permissions } from '@lanterds/types';
import {
    CustomerInfo,
    GetParcelSearchParameters,
    ParcelSearchSortItems,
    ScanSourceConnectors,
    SortDirection,
} from '@lanterds/types/build/parcelSearch';
import { defaultParcelSearchFormState } from './ParcelSearchForm/ParcelSearchForm';
import { Indexable } from 'src/interfaces/interfaces';
import ParcelSearchErrors from './formErrors';
import api from 'src/api';
import { extractUserSearchInput } from 'src/util/helpers';
import { MultiOffsetResult } from '@lanterds/types/build/result';
import { queryParamsEnabled } from 'src/util/featureFlags';
import { VariantType, SnackbarKey, useSnackbar } from 'notistack';
import { SnackbarVariantNames } from 'src/util/types';
import { getCustomersByPermission } from 'src/api/customer';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_SORT_DIRECTION = SortDirection.ASCENDING;
const DEFAULT_SORT_ORDER = ParcelSearchSortItems.PARCEL_CODE;

let currentRequestId = 0;

const DEFAULTS_PARAMS: Partial<GetParcelSearchParameters> = {
    pageSize: DEFAULT_PAGE_SIZE,
    sortDirection: DEFAULT_SORT_DIRECTION,
    sortOrder: DEFAULT_SORT_ORDER,
    sourceOffset: {},
};

interface CallbacksShape {
    successCallback?: { (): void | undefined };
    errorCallback?: { (): void | undefined };
}

export interface ParcelSearchContextShape {
    parcels: GeneralParcelInfo[] | null;
    setParcels: Dispatch<SetStateAction<GeneralParcelInfo[] | null>>;
    searchParams: Partial<GetParcelSearchParameters>;
    setSearchParams: Dispatch<SetStateAction<Partial<GetParcelSearchParameters>>>;
    customers: Array<CustomerInfo>;
    setCustomers: Dispatch<SetStateAction<Array<CustomerInfo>>>;
    customersAreLoading: boolean;
    setCustomersAreLoading: Dispatch<SetStateAction<boolean>>;
    parcelsAreLoading: boolean;
    setParcelsAreLoading: Dispatch<SetStateAction<boolean>>;
    lastResponseLessThanPage: boolean;
    setLastResponseLessThanPage: Dispatch<SetStateAction<boolean>>;
    shouldShowLoading: boolean;
    setShouldShowLoading: Dispatch<SetStateAction<boolean>>;
    fetchCustomers(): void;
    handleReset(): void;
    handleSubmit(params: Partial<GetParcelSearchParameters>, callbacks?: CallbacksShape): void;
    handleResultsPaging(): void;
    shouldShowResultsScrollError: boolean;
    warningText: string | null;
    formError: string | null;
    setFormError: Dispatch<SetStateAction<string | null>>;
    handleSortClick(column: string | null, direction: string | null): void;
    sortColumn: string | null;
    setSortColumn: Dispatch<SetStateAction<ParcelSearchSortItems>>;
    sortDirection: string | null;
    setSortDirection: Dispatch<SetStateAction<SortDirection>>;
    parcelListError: null | FmtpError | Error;
    layoutBodyRef: MutableRefObject<HTMLDivElement | null>;
    lastSourceOffset: Record<ScanSourceConnectors, number> | null;
    snackbarKeys: Partial<Record<VariantType, SnackbarKey[] | null>> | null;
    setSnackbarKeys: Dispatch<SetStateAction<Partial<Record<VariantType, SnackbarKey[] | null>> | null>>;
    closeSnackbars: (variantName: SnackbarVariantNames, snackbarList: SnackbarKey[] | null) => void;
}

const initialContext: ParcelSearchContextShape = {
    parcels: null,
    setParcels: (): void => undefined,
    searchParams: { ...DEFAULTS_PARAMS },
    setSearchParams: (): void => undefined,
    customers: [],
    setCustomers: (): void => undefined,
    customersAreLoading: false,
    setCustomersAreLoading: (): void => undefined,
    parcelsAreLoading: false,
    setParcelsAreLoading: (): void => undefined,
    shouldShowLoading: false,
    setShouldShowLoading: (): void => undefined,
    lastResponseLessThanPage: false,
    setLastResponseLessThanPage: (): void => undefined,
    fetchCustomers: (): void => undefined,
    handleReset: (): void => undefined,
    handleSubmit: (): void => undefined,
    handleResultsPaging: (): void => undefined,
    shouldShowResultsScrollError: false,
    warningText: null,
    formError: null,
    setFormError: (): void => undefined,
    handleSortClick: (): void => undefined,
    sortColumn: null,
    setSortColumn: (): void => undefined,
    sortDirection: null,
    setSortDirection: (): void => undefined,
    parcelListError: null,
    layoutBodyRef: createRef<HTMLDivElement>(),
    lastSourceOffset: null,
    snackbarKeys: null,
    setSnackbarKeys: (): void => undefined,
    closeSnackbars: (): void => undefined,
};

interface ParcelSearchContextProviderProps {
    children: ReactElement | ReactNode | ReactNode[];
}

export const ParcelSearchContext = React.createContext(initialContext);

export const ParcelSearchContextProvider = ({ children }: ParcelSearchContextProviderProps) => {
    const [parcels, setParcels] = useState<GeneralParcelInfo[] | null>(null);
    const [searchParams, setSearchParams] = useState<Partial<GetParcelSearchParameters>>({ ...DEFAULTS_PARAMS });
    const [customers, setCustomers] = useState<Array<CustomerInfo>>([]);
    const [customersAreLoading, setCustomersAreLoading] = useState(false);
    const [parcelsAreLoading, setParcelsAreLoading] = useState(false);
    const [lastResponseLessThanPage, setLastResponseLessThanPage] = useState(false);
    const [shouldShowLoading, setShouldShowLoading] = useState(false);
    const [shouldShowResultsScrollError, setShouldShowResultsScrollError] = useState(false);
    const [warningText, setWarningText] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [lastSourceOffset, setLastSourceOffset] = useState<Record<ScanSourceConnectors, number> | null>(null);
    const [sortColumn, setSortColumn] = useState<ParcelSearchSortItems>(ParcelSearchSortItems.PARCEL_CODE);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASCENDING);
    const [parcelListError, setParcelListError] = useState<null | FmtpError | Error>(null);
    const [snackbarKeys, setSnackbarKeys] = useState<Partial<Record<VariantType, SnackbarKey[] | null>> | null>(null);

    const layoutBodyRef = useRef<HTMLDivElement | null>(null);
    const { closeSnackbar } = useSnackbar();

    const requestCache: Indexable = {};

    /* This logic is not only supposed to close the warning messages and errors if the user clicks search or sort again (with a valid form), 
		 but is also supposed to hide any 'loading' snackbars if we can see parcels have been fetched or we are scrolling to fetch more.
		*/
    useEffect(() => {
        if (snackbarKeys) {
            // True when searching or sorting
            if (shouldShowLoading) {
                if (snackbarKeys.warning) {
                    closeSnackbars(SnackbarVariantNames.Warning, snackbarKeys.warning);
                }
                if (snackbarKeys.error) {
                    closeSnackbars(SnackbarVariantNames.Error, snackbarKeys.error);
                }

                if (parcels && parcels.length) {
                    if (snackbarKeys.loading) {
                        closeSnackbars(SnackbarVariantNames.Loading, snackbarKeys.loading);
                    }
                }
            } else {
                if (!parcelsAreLoading && parcels && parcels.length) {
                    if (snackbarKeys.loading) {
                        closeSnackbars(SnackbarVariantNames.Loading, snackbarKeys.loading);
                    }
                }
            }

            // True when scrolling results and fetching additional results
            if (parcelsAreLoading) {
                if (snackbarKeys.warning) {
                    closeSnackbars(SnackbarVariantNames.Warning, snackbarKeys.warning);
                }
                if (snackbarKeys.error) {
                    closeSnackbars(SnackbarVariantNames.Error, snackbarKeys.error);
                }
            } else {
                if (snackbarKeys.loading) {
                    closeSnackbars(SnackbarVariantNames.Loading, snackbarKeys.loading);
                }
            }
        }
    }, [shouldShowLoading, snackbarKeys, lastResponseLessThanPage, formError, parcelsAreLoading]);

    const closeSnackbars = (variantName: SnackbarVariantNames, snackbarList: SnackbarKey[] | null) => {
        if (snackbarList) {
            for (const snackbar of snackbarList) {
                closeSnackbar(snackbar);
            }
            setSnackbarKeys({ ...snackbarKeys, [variantName]: null });
        }
    };

    const isDuplicateRequest = (key: string) => {
        if (key in requestCache) {
            return true;
        } else {
            requestCache[key] = true;
            return false;
        }
    };

    async function fetchCustomers() {
        if (!customersAreLoading && customers.length === 0) {
            setCustomersAreLoading(true);

            getCustomersByPermission(Permissions.parcelSearch)
                .then((customers) => {
                    customers.sort((a: CustomerInfo, b: CustomerInfo) =>
                        a.customerName.toLocaleLowerCase().localeCompare(b.customerName.toLocaleLowerCase()),
                    );

                    setCustomers(customers);
                })
                .catch((error) => {
                    console.error(error);
                    setParcelListError(error);
                })
                .finally(() => setCustomersAreLoading(false));
        }
    }

    async function fetchParcels(params: Partial<GetParcelSearchParameters>, callbacks?: CallbacksShape) {
        setSearchParams(params);
        setParcelsAreLoading(true);
        const parcelSearchResults: MultiOffsetResult<GeneralParcelInfo> = await api.parcel
            .postParcelsSearch(params)
            .catch((err) => {
                console.error(err);
                setFormError(ParcelSearchErrors.genericError);
                setParcelListError(err);
                if (callbacks?.errorCallback) callbacks.errorCallback();
                return [];
            });
        setParcelsAreLoading(false);

        if (parcelSearchResults.error) {
            const error = parcelSearchResults.error;
            console.error(error);
            setParcelListError(error);
            if (callbacks?.errorCallback) callbacks.errorCallback();
            return [];
        }

        const { data, warning, offsets } = parcelSearchResults;

        if (warning) {
            if (warning instanceof FmtpError) {
                console.warn(warning.memo);
                setWarningText(warning.memo);
            }
            if (warning instanceof Array) {
                if (warning.length) setWarningText(warning[0].memo);

                warning.forEach((warningItem: FmtpError) => {
                    console.warn(warningItem.memo);
                });
            }
        }

        if (callbacks?.successCallback) callbacks.successCallback();

        if (data) {
            setLastResponseLessThanPage(data.length < DEFAULT_PAGE_SIZE);
            setLastSourceOffset(offsets);

            return data;
        }
        return [];
    }

    const handleSubmit = async (params: GetParcelSearchParameters, callbacks?: CallbacksShape) => {
        const thisRequestId = ++currentRequestId;

        resetParcels();

        const callbacksFromForm = callbacks ? callbacks : {};
        const tmpParams = {
            ...params,
            ...DEFAULTS_PARAMS,
        };

        if (queryParamsEnabled) {
            const queryParams = new URLSearchParams();
            const params = extractUserSearchInput(tmpParams);

            Object.entries(params).forEach((entry) => {
                const [key, value] = entry;

                if (value) {
                    queryParams.set(key, value.toString());
                }
            });

            const updatedUrl = `${window.location.protocol}//${window.location.host}${
                window.location.pathname
            }?${queryParams.toString()}`;
            window.history.pushState({ path: updatedUrl }, '', updatedUrl);
        }

        const tmpCallbacks = {
            errorCallback: () => {
                if (callbacksFromForm?.errorCallback) callbacksFromForm.errorCallback();
            },
            successCallback: () => {
                if (callbacksFromForm?.successCallback) callbacksFromForm.successCallback();
            },
        };

        setShouldShowLoading(true);

        const data = await fetchParcels(tmpParams, tmpCallbacks);

        setShouldShowLoading(false);
        currentRequestId === thisRequestId && setParcels(data);
    };

    const resetParcels = () => {
        setParcels(null);
        setLastResponseLessThanPage(false);
        setShouldShowLoading(false);
        setShouldShowResultsScrollError(false);
        setWarningText(null);
        setFormError(null);
        setSortColumn(DEFAULT_SORT_ORDER);
        setSortDirection(DEFAULT_SORT_DIRECTION);
    };

    const handleReset = () => {
        resetParcels();
        setSearchParams({
            ...defaultParcelSearchFormState,
            ...DEFAULTS_PARAMS,
        });

        currentRequestId++;

        if (queryParamsEnabled) {
            const updatedUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
            window.history.pushState({ path: updatedUrl }, '', updatedUrl);
        }
    };

    const handleResultsPaging = async () => {
        const tmpParams: Partial<GetParcelSearchParameters> = {
            ...searchParams,
            sourceOffset: lastSourceOffset ?? DEFAULTS_PARAMS.sourceOffset,
        };

        const callbacks = {
            errorCallback: () => {
                setShouldShowResultsScrollError(true);
            },
        };

        const isDupe = isDuplicateRequest(JSON.stringify(tmpParams?.sourceOffset) || '');
        const shouldFetch = !parcelsAreLoading && !isDupe && !lastResponseLessThanPage;

        if (shouldFetch) {
            const thisRequestId = ++currentRequestId;
            const data = await fetchParcels(tmpParams, callbacks);
            currentRequestId === thisRequestId && setParcels([...(parcels ?? []), ...data]);
        }
    };

    const handleSortClick = async (column: ParcelSearchSortItems, direction: SortDirection) => {
        const thisRequestId = ++currentRequestId;

        resetParcels();
        setShouldShowLoading(true);
        const tmpParams: Partial<GetParcelSearchParameters> = {
            ...searchParams,
            sourceOffset: {} as Record<ScanSourceConnectors, number>,
            sortDirection: direction,
            sortOrder: column,
            pageSize: DEFAULT_PAGE_SIZE,
        };

        const tmpCallbacks = {
            successCallback: () => {
                setShouldShowLoading(false);
            },
        };

        setSortColumn(column);
        setSortDirection(direction);

        const data = await fetchParcels(tmpParams, tmpCallbacks);
        setShouldShowLoading(false);

        setSearchParams(tmpParams);

        currentRequestId === thisRequestId && setParcels(data);
    };

    return (
        <ParcelSearchContext.Provider
            value={{
                parcels,
                setParcels,
                searchParams,
                setSearchParams,
                customers,
                setCustomers,
                customersAreLoading,
                setCustomersAreLoading,
                parcelsAreLoading,
                setParcelsAreLoading,
                lastResponseLessThanPage,
                setLastResponseLessThanPage,
                shouldShowLoading,
                setShouldShowLoading,
                fetchCustomers,
                handleReset,
                handleSubmit,
                handleResultsPaging,
                shouldShowResultsScrollError,
                warningText,
                formError,
                setFormError,
                handleSortClick,
                sortColumn,
                setSortColumn,
                sortDirection,
                setSortDirection,
                parcelListError,
                layoutBodyRef,
                lastSourceOffset,
                snackbarKeys,
                setSnackbarKeys,
                closeSnackbars,
            }}
        >
            {children}
        </ParcelSearchContext.Provider>
    );
};
